import { useState } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAddPapers } from "@/hooks/usePapers";

const schema = z.object({
  title: z.string().min(1, "Title is required"),
});

interface Props {
  open: boolean;
  onClose: () => void;
  conceptId: string;
  role: string;
}

export function AddPaperDialog({ open, onClose, conceptId, role }: Props) {
  const [title, setTitle] = useState("");
  const [authors, setAuthors] = useState("");
  const [year, setYear] = useState("");
  const [journal, setJournal] = useState("");
  const [doi, setDoi] = useState("");
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const addPapers = useAddPapers(conceptId);

  function reset() {
    setTitle("");
    setAuthors("");
    setYear("");
    setJournal("");
    setDoi("");
    setUrl("");
    setError("");
  }

  async function handleSubmit() {
    const result = schema.safeParse({ title });
    if (!result.success) {
      setError(result.error.issues[0].message);
      return;
    }

    try {
      await addPapers.mutateAsync({
        papers: [
          {
            title,
            authors: authors
              ? authors.split(",").map((a) => a.trim())
              : undefined,
            year: year ? parseInt(year, 10) : undefined,
            journal: journal || undefined,
            doi: doi || undefined,
            url: url || undefined,
          },
        ],
        role,
        added_source: "manual",
      });
      reset();
      onClose();
    } catch {
      setError("Failed to add paper.");
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Paper</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="paper-title">Title *</Label>
            <Input
              id="paper-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="paper-authors">Authors (comma-separated)</Label>
              <Input
                id="paper-authors"
                value={authors}
                onChange={(e) => setAuthors(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="paper-year">Year</Label>
              <Input
                id="paper-year"
                type="number"
                value={year}
                onChange={(e) => setYear(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="paper-journal">Journal</Label>
              <Input
                id="paper-journal"
                value={journal}
                onChange={(e) => setJournal(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="paper-doi">DOI</Label>
              <Input
                id="paper-doi"
                value={doi}
                onChange={(e) => setDoi(e.target.value)}
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="paper-url">URL</Label>
            <Input
              id="paper-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={addPapers.isPending}>
            {addPapers.isPending ? "Adding..." : "Add Paper"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
